import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import {
  applyAssetRename,
  AssetRenameCategory,
  AssetRenameError,
  AssetRenameImpact,
  previewAssetRename,
} from '../api/assetRename';

interface AssetRenameDialogProps {
  open: boolean;
  projectId: string;
  category: AssetRenameCategory;
  oldName: string;
  onClose: () => void;
  onApplied: (impact: AssetRenameImpact) => void;
}

type Step = 1 | 2;

const CATEGORY_KEYS: Record<AssetRenameCategory, string> = {
  character: 'assetRename.categoryCharacter',
  scene: 'assetRename.categoryScene',
  prop: 'assetRename.categoryProp',
};

const AssetRenameDialog: React.FC<AssetRenameDialogProps> = ({
  open,
  projectId,
  category,
  oldName,
  onClose,
  onApplied,
}) => {
  const { t } = useTranslation('common');
  const [step, setStep] = useState<Step>(1);
  const [newName, setNewName] = useState('');
  const [typedConfirm, setTypedConfirm] = useState('');
  const [preview, setPreview] = useState<AssetRenameImpact | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyError, setApplyError] = useState<{ msg: string; taskIds: string[] } | null>(null);

  const categoryLabel = t(CATEGORY_KEYS[category]);

  const resetAll = useCallback(() => {
    setStep(1);
    setNewName('');
    setTypedConfirm('');
    setPreview(null);
    setPreviewLoading(false);
    setPreviewError(null);
    setApplyLoading(false);
    setApplyError(null);
  }, []);

  useEffect(() => {
    if (open) {
      resetAll();
    }
  }, [open, oldName, category, resetAll]);

  const runPreview = useCallback(async () => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) return;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);
    try {
      const impact = await previewAssetRename(projectId, {
        category,
        oldName,
        newName: trimmed,
      });
      setPreview(impact);
    } catch (err: unknown) {
      if (err instanceof AssetRenameError) {
        setPreviewError(err.message);
      } else {
        setPreviewError(err instanceof Error ? err.message : t('previewFailed'));
      }
    } finally {
      setPreviewLoading(false);
    }
  }, [projectId, category, oldName, newName, t]);

  const blocking = useMemo(() => {
    if (!preview) return null;
    if (preview.advancedAnalysisBlocking) return { kind: 'advanced' as const };
    const tasks = preview.imageTasksBlocking || [];
    if (tasks.length > 0) return { kind: 'tasks' as const, taskIds: tasks };
    return null;
  }, [preview]);

  const nothingFound = useMemo(() => {
    if (!preview) return false;
    return (
      preview.analysisRows === 0 &&
      preview.shotAssetRefs === 0 &&
      preview.shotVersionAssetRefs === 0 &&
      preview.assetReviews === 0 &&
      preview.assetSearchTasks === 0 &&
      preview.styleTests === 0 &&
      preview.assetComments === 0 &&
      !preview.disabledAssetsUpdated
    );
  }, [preview]);

  const canGoStep2 = !!preview && !previewLoading && !blocking && !nothingFound;

  const handleNext = () => {
    if (!canGoStep2) return;
    setStep(2);
    setTypedConfirm('');
    setApplyError(null);
  };

  const handleBack = () => {
    setStep(1);
    setApplyError(null);
  };

  const handleApply = useCallback(async () => {
    if (applyLoading) return;
    if (typedConfirm.trim() !== newName.trim()) return;
    setApplyLoading(true);
    setApplyError(null);
    try {
      const impact = await applyAssetRename(projectId, {
        category,
        oldName,
        newName: newName.trim(),
      });
      onApplied(impact);
      onClose();
    } catch (err: unknown) {
      if (err instanceof AssetRenameError) {
        const blockedTaskIds = err.taskIds;
        setApplyError({ msg: err.message, taskIds: blockedTaskIds });
        if (err.status === 409) {
          setPreview((p) => p ? { ...p, imageTasksBlocking: blockedTaskIds } : p);
          setStep(1);
        }
      } else {
        setApplyError({ msg: err instanceof Error ? err.message : t('renameFailed'), taskIds: [] });
      }
    } finally {
      setApplyLoading(false);
    }
  }, [applyLoading, typedConfirm, newName, projectId, category, oldName, onApplied, onClose, t]);

  const trimmedNew = newName.trim();
  const nameInvalid = !trimmedNew
    || trimmedNew === oldName
    || trimmedNew.includes('|')
    || /\s/.test(trimmedNew);

  const stepOneFooter = (
    <>
      <button className="btn btn-secondary" onClick={onClose} disabled={previewLoading}>
        {t('cancel')}
      </button>
      {preview ? (
        <button
          className="btn btn-primary"
          onClick={handleNext}
          disabled={!canGoStep2}
          title={blocking ? t('assetRename.blockConflict') : nothingFound ? t('assetRename.blockNotFound') : ''}
        >
          {t('assetRename.nextStepConfirm')}
        </button>
      ) : (
        <button
          className="btn btn-primary"
          onClick={runPreview}
          disabled={nameInvalid || previewLoading}
        >
          {previewLoading && <span className="loading-spinner" />}
          {t('assetRename.previewImpact')}
        </button>
      )}
    </>
  );

  const stepTwoFooter = (
    <>
      <button className="btn btn-secondary" onClick={handleBack} disabled={applyLoading}>
        {t('back')}
      </button>
      <button
        className="btn btn-danger"
        onClick={handleApply}
        disabled={typedConfirm.trim() !== trimmedNew || applyLoading}
      >
        {applyLoading && <span className="loading-spinner" />}
        {t('assetRename.executeRename')}
      </button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={() => { if (!applyLoading && !previewLoading) onClose(); }}
      title={step === 1
        ? t('assetRename.renameTitle', { category: categoryLabel, oldName })
        : t('assetRename.confirmRenameTitle', { oldName, newName: trimmedNew })}
      width="520px"
      footer={step === 1 ? stepOneFooter : stepTwoFooter}
    >
      {step === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <div>
            <label style={labelStyle}>{t('assetRename.newNameLabel')}</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => { setNewName(e.target.value); setPreview(null); }}
              placeholder={t('assetRename.newNamePlaceholder', { category: categoryLabel })}
              style={inputStyle}
              autoFocus
              disabled={previewLoading}
            />
            {trimmedNew && trimmedNew === oldName && (
              <p style={hintError}>{t('assetRename.sameNameError')}</p>
            )}
            {trimmedNew && trimmedNew.includes('|') && (
              <p style={hintError}>{t('assetRename.pipeCharError')}</p>
            )}
            {trimmedNew && /\s/.test(trimmedNew) && (
              <p style={hintError}>{t('assetRename.whitespaceError')}</p>
            )}
          </div>

          {previewError && <div style={bannerError}>{previewError}</div>}

          {preview && !blocking && !nothingFound && (
            <div style={previewBox}>
              <h4 style={{ margin: '0 0 0.4rem', fontSize: '0.88rem', fontWeight: 600 }}>{t('assetRename.impactPreviewTitle')}</h4>
              <ImpactRow label={t('assetRename.impactAnalysis')} value={preview.analysisRows} />
              <ImpactRow label={t('assetRename.impactShotAssetRefs')} value={preview.shotAssetRefs} />
              <ImpactRow label={t('assetRename.impactShotVersionRefs')} value={preview.shotVersionAssetRefs} />
              {preview.disabledAssetsUpdated && <ImpactRow label={t('assetRename.impactDisabledAssets')} value={t('assetRename.impactDisabledAssetsValue')} />}
              <ImpactRow label={t('assetRename.impactAssetReviews')} value={preview.assetReviews} muted />
              <ImpactRow label={t('assetRename.impactSearchTasks')} value={preview.assetSearchTasks} muted />
              <ImpactRow label={t('assetRename.impactStyleTests')} value={preview.styleTests} muted />
              <ImpactRow label={t('assetRename.impactAssetComments')} value={preview.assetComments} muted />
              {preview.promptsStale > 0 && (
                <div style={warnBanner}>
                  {t('assetRename.promptsStaleWarning', { count: preview.promptsStale, oldName })}
                </div>
              )}
            </div>
          )}

          {preview && blocking?.kind === 'advanced' && (
            <div style={bannerError}>
              {t('assetRename.blockAdvancedAnalysis')}
            </div>
          )}
          {preview && blocking?.kind === 'tasks' && (
            <div style={bannerError}>
              {t('assetRename.blockImageTasks', { count: blocking.taskIds.length })}
              <div style={{ marginTop: 6, fontSize: '0.72rem', color: 'var(--text-3)' }}>
                {t('assetRename.taskIdLabel', { ids: blocking.taskIds.join(', ') })}
              </div>
            </div>
          )}
          {preview && nothingFound && (
            <div style={bannerError}>
              {t('assetRename.nothingFound', { oldName })}
            </div>
          )}
        </div>
      )}

      {step === 2 && preview && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <div style={{ padding: '0.75rem', background: 'var(--surface-2)', borderRadius: 6, fontSize: '0.85rem' }}>
            <div dangerouslySetInnerHTML={{
              __html: t('assetRename.aboutToRename', { category: `<strong>${categoryLabel}</strong>`, oldName: `<strong>${oldName}</strong>`, newName: `<strong>${trimmedNew}</strong>` })
            }} />
            <div style={{ marginTop: 6, color: 'var(--text-2)' }}>
              {t('assetRename.impactSummary', { analysisRows: preview.analysisRows, shotRefs: preview.shotAssetRefs, versionRefs: preview.shotVersionAssetRefs })}
              {preview.promptsStale > 0 && t('assetRename.promptsStaleExtra', { count: preview.promptsStale })}
            </div>
          </div>
          <div>
            <label style={labelStyle}>{t('assetRename.typeNewNameToConfirm')} <span style={{ color: 'var(--danger)' }}>{trimmedNew}</span></label>
            <input
              type="text"
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              placeholder={trimmedNew}
              style={inputStyle}
              autoFocus
              disabled={applyLoading}
            />
          </div>
          {applyError && (
            <div style={bannerError}>
              {applyError.msg}
              {applyError.taskIds.length > 0 && (
                <div style={{ marginTop: 6, fontSize: '0.72rem', color: 'var(--text-3)' }}>
                  {t('assetRename.taskIdLabel', { ids: applyError.taskIds.join(', ') })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-2)', marginBottom: 4,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.45rem 0.6rem', fontSize: '0.88rem',
  border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface)', color: 'var(--text)',
  boxSizing: 'border-box',
};
const hintError: React.CSSProperties = {
  margin: '4px 0 0', fontSize: '0.72rem', color: 'var(--danger)',
};
const bannerError: React.CSSProperties = {
  padding: '0.55rem 0.7rem', borderRadius: 4, fontSize: '0.8rem',
  background: 'rgba(220, 38, 38, 0.08)', color: 'var(--danger)', border: '1px solid rgba(220, 38, 38, 0.25)',
};
const warnBanner: React.CSSProperties = {
  marginTop: '0.5rem', padding: '0.5rem 0.65rem', borderRadius: 4, fontSize: '0.78rem',
  background: 'rgba(221, 91, 0, 0.08)', color: 'var(--warning)', border: '1px solid rgba(221, 91, 0, 0.25)',
};
const previewBox: React.CSSProperties = {
  padding: '0.7rem 0.8rem', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface-2)',
};

const ImpactRow: React.FC<{ label: string; value: number | string; muted?: boolean }> = ({ label, value, muted }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '2px 0', fontSize: '0.82rem', color: muted ? 'var(--text-3)' : 'var(--text-2)',
  }}>
    <span>{label}</span>
    <span style={{ fontWeight: muted ? 400 : 600 }}>{value}</span>
  </div>
);

export default AssetRenameDialog;
